import {DestroyRef, Injectable} from '@angular/core';
import {SocketMessengerService, WebSocketAsyncMessage} from '@ibsoftware/ng-web-socket';
import {firstValueFrom} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CreateBetslipDto} from '../player-api/models/create-betslip-dto';
import {PlayerBetslipDetailsDto} from '../player-api/models/player-betslip-details-dto';
import {PlayerBetslipOverviewPageFilterDto} from '../player-api/models/player-betslip-overview-page-filter-dto';
import {PlayerBetslipOverviewDto} from '../player-api/models/player-betslip-overview-dto';
import {BetslipNumDto} from '../player-api/models/betslip-num-dto';
import {BetslipNumWithPlayerIdDto} from '../player-api/models/betslip-num-with-player-id-dto';
import {BetslipSelectionDto} from '@/api/player-api/models/betslip-selection-dto';
import {BetslipSelectionsDto} from '@/api/player-api/models/betslip-selections-dto';
import {FrontEndMatchDto} from '@/api/player-api/models/front-end-match-dto';
import {BetslipRiskInfoDto} from '@/api/player-api/models/betslip-risk-info-dto';
import {RegisterBetslipResponseDto} from '@/api/player-api/models/register-betslip-response-dto';

@Injectable({providedIn: 'root'})
export class BetslipWsApi {

	constructor(
		private socketMessengerService: SocketMessengerService
	) {
	}

	/**
	 * Returns betslip details for created betslip.
	 * @param betslipCreatePayload - Betslip for creation
	 * @param destroyRef - Used to unsubscribe when component or service is destroyed if the call is not yet completed at that moment
	 */
	public registerBetslip(betslipCreatePayload: CreateBetslipDto, destroyRef: DestroyRef): Promise<RegisterBetslipResponseDto> {
		const message = new WebSocketAsyncMessage({
			name: 'createBetslip',
			payload: betslipCreatePayload
		})
		const observable = this.socketMessengerService
			.sendMessage<RegisterBetslipResponseDto>(message)
			.pipe(takeUntilDestroyed(destroyRef))
		return firstValueFrom(observable)
	}

	/**
	 * Returns betslip risk info for betslip.
	 * @param betslipCreatePayload - Betslip against which we want risk info
	 * @param destroyRef - Used to unsubscribe when component or service is destroyed if the call is not yet completed at that moment
	 */
	public betslipRiskInfo(betslipCreatePayload: CreateBetslipDto, destroyRef: DestroyRef): Promise<BetslipRiskInfoDto> {
		const message = new WebSocketAsyncMessage({
			name: 'betslipRiskInfo',
			payload: betslipCreatePayload
		})
		const observable = this.socketMessengerService
			.sendMessage<BetslipRiskInfoDto>(message)
			.pipe(takeUntilDestroyed(destroyRef))
		return firstValueFrom(observable)
	}

	/**
	 * Returns list of player betslips, for given filter
	 * @param betslipOverviewPayload - Filter to get betslips
	 * @param destroyRef - Used to unsubscribe when component or service is destroyed if the call is not yet completed at that moment
	 */
	public betslipOverview(betslipOverviewPayload: PlayerBetslipOverviewPageFilterDto, destroyRef: DestroyRef): Promise<PlayerBetslipOverviewDto> {
		const message = new WebSocketAsyncMessage({
			name: 'betslipOverview',
			payload: betslipOverviewPayload
		})
		const observable = this.socketMessengerService
			.sendMessage<PlayerBetslipOverviewDto>(message)
			.pipe(takeUntilDestroyed(destroyRef))
		return firstValueFrom(observable)
	}

	/**
	 * Returns betslip details for given betslip number.
	 * @param betslipDetailsPayload - Betslip details payload
	 * @param destroyRef - Used to unsubscribe when component or service is destroyed if the call is not yet completed at that moment
	 */
	public betslipDetails(betslipDetailsPayload: BetslipNumDto, destroyRef: DestroyRef): Promise<PlayerBetslipDetailsDto> {
		const message = new WebSocketAsyncMessage({
			name: 'betslipDetails',
			payload: betslipDetailsPayload
		})
		const observable = this.socketMessengerService
			.sendMessage<PlayerBetslipDetailsDto>(message)
			.pipe(takeUntilDestroyed(destroyRef))
		return firstValueFrom(observable)
	}

	/**
	 * Deletes betslip for given betslip number.
	 * @param betslipDetailsPayload - Betslip details payload
	 * @param destroyRef - Used to unsubscribe when component or service is destroyed if the call is not yet completed at that moment
	 */
	public hideBetslip(betslipDetailsPayload: BetslipNumDto, destroyRef: DestroyRef): Promise<BetslipNumWithPlayerIdDto> {
		const message = new WebSocketAsyncMessage({
			name: 'hideBetslip',
			payload: betslipDetailsPayload
		})
		const observable = this.socketMessengerService
			.sendMessage<BetslipNumWithPlayerIdDto>(message)
			.pipe(takeUntilDestroyed(destroyRef))
		return firstValueFrom(observable)
	}

	/**
	 * Add selection (match/odds) to betslip selections update subscription
	 */
	public addBetslipSelectionMonitoring(betslipSelectionsDto: BetslipSelectionsDto,
																			 destroyRef: DestroyRef): Promise<FrontEndMatchDto[]> {
		const message = new WebSocketAsyncMessage({
			name: 'addBetslipSelection',
			payload: betslipSelectionsDto
		})
		const observable = this.socketMessengerService
			.sendMessage<FrontEndMatchDto[]>(message)
			.pipe(takeUntilDestroyed(destroyRef))
		return firstValueFrom(observable)
	}

	/**
	 * Remove selection (match/odds) from betslip selections update subscription
	 */
	public removeBetslipSelectionMonitoring(betslipSelectionDto: BetslipSelectionDto,
																			 destroyRef: DestroyRef): Promise<void> {
		const message = new WebSocketAsyncMessage({
			name: 'removeBetslipSelection',
			payload: betslipSelectionDto
		})
		const observable = this.socketMessengerService
			.sendMessage<void>(message)
			.pipe(takeUntilDestroyed(destroyRef))
		return firstValueFrom(observable)
	}

	/**
	 * Clear all selections from betslip
	 */
	public clearBetslipSelectionsFromMonitoring(destroyRef: DestroyRef): Promise<void> {
		const message = new WebSocketAsyncMessage({
			name: 'clearBetslipSelections'
		})
		const observable = this.socketMessengerService
			.sendMessage<void>(message)
			.pipe(takeUntilDestroyed(destroyRef))
		return firstValueFrom(observable)
	}
}
